// src/core/auth/pages/SignInPage.jsx
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMe } from 'core/me/provider/MeProvider';
import { useCompanySettings } from 'core/companySettings';

import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Icon, InputAdornment } from '@mui/material';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

import bgImage from 'assets/images/bg-sign-in-basic.jpeg';
import { useLogin } from '../hooks/useLogin';
import { AuthBasicLayout } from '../layouts/AuthBasicLayout';

import { authApi } from '../api/authApi';
import { EmailVerificationScreen } from './components/EmailVerificationScreen';
import { uiBus } from 'shared/api/uiBus';

const SUPPORT_PHONE = '05421162306'; // görünen
const SUPPORT_WA_E164 = '905421162306'; // whatsapp için (TR)

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());
}

function unwrapData(resp) {
  return resp?.data ?? resp;
}

function toUiMessage(e) {
  const status = e?.response?.status ?? e?.status;
  const data = e?.response?.data ?? e?.data;
  const code = data?.code ?? e?.code;
  const path = data?.path ?? e?.config?.url ?? e?.url ?? '';

  if (status === 401 || code === 'INVALID_CREDENTIALS') {
    return 'Kullanıcı adı/e-posta veya şifre yanlış.';
  }

  // validation: { errors: {field: msg}}
  if (data?.errors && typeof data.errors === 'object') {
    const joined = Object.values(data.errors).filter(Boolean).join(' • ');
    if (joined) return joined;
  }

  if (status === 400 && String(path).includes('/api/v1/auth/login')) {
    return 'Kullanıcı adı/e-posta veya şifre yanlış.';
  }

  const m = data?.message ?? e?.message ?? String(e ?? '');
  if (String(m).toLowerCase().includes('unauthorized')) {
    return 'Kullanıcı adı/e-posta veya şifre yanlış.';
  }

  return m || 'Giriş yapılamadı. Lütfen tekrar deneyin.';
}

function isMobileUA() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

function PlanBlockedModal({ open, onClose, code, message, phone, waE164 }) {
  const theme = useMuiTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = isXs || isMobileUA();

  const title =
    code === 'TRIAL_EXPIRED'
      ? 'Deneme süreniz bitti'
      : code === 'PAYMENT_DUE'
      ? 'Ödeme bekleniyor'
      : code === 'PLAN_EXPIRED'
      ? 'Plan süreniz bitti'
      : 'Plan erişimi kapalı';

  const handleContact = async () => {
    if (isMobile) {
      const text = encodeURIComponent(
        'Merhaba, SolisUp plan yükseltme/yenileme için destek almak istiyorum.'
      );
      window.open(`https://wa.me/${waE164}?text=${text}`, '_blank', 'noopener,noreferrer');
      return;
    }

    // desktop: kopyala
    try {
      await navigator.clipboard.writeText(phone);
      uiBus.emit('toast', { type: 'success', message: 'Numara kopyalandı.' });
    } catch {
      // izin yoksa sadece gösteriyoruz zaten
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <MDBox
        sx={{
          px: 2.25,
          pt: 1.75,
          pb: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
        }}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDBox
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              background: alpha(theme.palette.warning.main, 0.14),
              color: theme.palette.warning.main,
            }}
          >
            <Icon sx={{ fontSize: 20 }}>report</Icon>
          </MDBox>
          <MDTypography variant="button" fontWeight="bold">
            {title}
          </MDTypography>
        </MDBox>

        <IconButton onClick={onClose} size="small" aria-label="close">
          <Icon fontSize="small">close</Icon>
        </IconButton>
      </MDBox>

      <DialogContent>
        <MDTypography variant="button" color="text" sx={{ opacity: 0.85 }}>
          {message || 'Devam etmek için planınızı aktif etmeniz gerekiyor.'}
        </MDTypography>

        {!isMobile && (
          <MDBox
            sx={{
              mt: 2,
              p: 1.25,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
              background: alpha(theme.palette.background.default, 0.6),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <MDBox>
              <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                Destek hattı
              </MDTypography>
              <MDTypography variant="button" fontWeight="bold">
                {phone}
              </MDTypography>
            </MDBox>

            <MDButton
              variant="outlined"
              color="dark"
              size="small"
              onClick={handleContact}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Kopyala
            </MDButton>
          </MDBox>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <MDButton
          variant="outlined"
          color="dark"
          onClick={onClose}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Kapat
        </MDButton>

        <MDButton
          variant="contained"
          color="info"
          onClick={handleContact}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          startIcon={<Icon>chat</Icon>}
        >
          İletişime Geç
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default function SignInPage() {
  const login = useLogin();
  const nav = useNavigate();

  const { refresh: refreshMe } = useMe();
  const { refresh: refreshSettings } = useCompanySettings();

  const [pendingAuth, setPendingAuth] = useState(null);

  const [rememberMe, setRememberMe] = useState(true);
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [planModal, setPlanModal] = useState({ open: false, code: '', message: '' });

  const [verify, setVerify] = useState({
    open: false,
    loginId: '',
    email: '',
    emailMasked: '',
    resolving: false,
  });

  const canSubmit = useMemo(() => {
    if (verify.open) return false;
    return loginId.trim().length > 0 && password.length > 0 && !loading;
  }, [verify.open, loginId, password, loading]);

  async function openVerifyFor(input) {
    const id = String(input || '').trim();
    const emailDirect = isValidEmail(id);

    setVerify((s) => ({
      ...s,
      open: true,
      loginId: id,
      email: emailDirect ? id : '',
      emailMasked: '',
      resolving: !emailDirect,
    }));

    if (emailDirect) return;

    try {
      const resp = await authApi.resolveLoginId({ loginId: id });
      const data = unwrapData(resp);

      setVerify((s) => ({
        ...s,
        email: data?.email || '',
        emailMasked: data?.emailMasked || '',
        resolving: false,
      }));
    } catch {
      setVerify((s) => ({ ...s, resolving: false }));
    }
  }

  async function hydrateAfterLogin() {
    await refreshMe();
    await refreshSettings();
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);

    const id = loginId.trim();
    if (!id) {
      const m = 'Lütfen e-posta adresinizi veya kullanıcı adınızı girin.';
      setErr(m);
      uiBus.emit('toast', { type: 'error', message: m });
      return;
    }
    if (!password) {
      const m = 'Lütfen şifrenizi girin.';
      setErr(m);
      uiBus.emit('toast', { type: 'error', message: m });
      return;
    }

    setLoading(true);
    try {
      const r = await login({ loginId: id, password, rememberMe });

      if (r?.status === 'PENDING_EMAIL') {
        setPendingAuth({ loginId: id, rememberMe });
        await openVerifyFor(id);
        return;
      }

      await hydrateAfterLogin();
      nav('/app', { replace: true });
    } catch (e2) {
      const status = e2?.response?.status ?? e2?.status;
      const data = e2?.response?.data ?? e2?.data;
      const code = data?.code ?? e2?.code;
      const msg = data?.message ?? toUiMessage(e2);

      const planCodes = new Set(['TRIAL_EXPIRED', 'PLAN_EXPIRED', 'PAYMENT_DUE']);

      if (status === 402 && planCodes.has(code)) {
        setPlanModal({ open: true, code, message: msg });
      } else {
        setErr(msg);
        uiBus.emit('toast', { type: 'error', message: msg });
      }
    } finally {
      setLoading(false);
    }
  }

  const shownEmail =
    verify.email || verify.emailMasked || (isValidEmail(verify.loginId) ? verify.loginId : '');

  const inputsDisabled = loading || verify.open;

  return (
    <AuthBasicLayout image={bgImage} footerVariant="light">
      <Card
        sx={{
          overflow: 'hidden',
          borderRadius: { xs: '18px', sm: '22px' },
          width: '100%',
          maxWidth: 480,
          backgroundColor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.55)',
          boxShadow: '0 30px 90px rgba(0,0,0,0.22)',
        }}
      >
        {/* Header */}
        <MDBox
          px={{ xs: 2.5, sm: 3.25 }}
          pt={{ xs: 2.75, sm: 3.25 }}
          pb={{ xs: 2.0, sm: 2.25 }}
          textAlign="left"
          sx={{
            position: 'relative',
            background: 'linear-gradient(135deg, rgba(46,139,255,0.16), rgba(43,83,134,0.06))',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1.25}>
            <MDBox
              width={44}
              height={44}
              sx={{
                borderRadius: '14px',
                display: 'grid',
                placeItems: 'center',
                background: 'linear-gradient(135deg, #2b5386, #2E8BFF)',
                boxShadow: '0 12px 30px rgba(46,139,255,0.25)',
              }}
            >
              <Icon sx={{ color: '#fff', fontSize: 22 }}>bolt</Icon>
            </MDBox>

            <MDBox>
              <MDTypography variant="h5" fontWeight="bold" color="dark" sx={{ lineHeight: 1.1 }}>
                SolisUp
              </MDTypography>
              <MDTypography variant="button" color="text" sx={{ opacity: 0.7 }}>
                {verify.open ? 'E-postanı doğrula' : 'Pilates yönetim paneline giriş yap'}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Body */}
        <MDBox pt={{ xs: 2.25, sm: 3 }} pb={{ xs: 2.25, sm: 3 }} px={{ xs: 2.25, sm: 3 }}>
          {verify.open ? (
            <EmailVerificationScreen
              email={shownEmail}
              loginId={verify.loginId}
              onVerify={({ loginId, code }) => authApi.verifyEmail({ loginId, code })}
              onResend={({ loginId }) => authApi.resendVerifyEmail({ loginId })}
              onVerifiedSuccess={async () => {
                const creds = pendingAuth || { loginId: verify.loginId, rememberMe };

                if (!password) {
                  const m = 'Otomatik giriş için şifre bulunamadı. Lütfen tekrar giriş yap.';
                  uiBus.emit('toast', { type: 'error', message: m });
                  throw { message: m };
                }

                const r2 = await login({ ...creds, password });
                if (r2?.status === 'PENDING_EMAIL') {
                  const m =
                    'E-posta doğrulaması tamamlanmadı gibi görünüyor. Lütfen tekrar deneyin.';
                  uiBus.emit('toast', { type: 'error', message: m });
                  throw { message: m };
                }

                await hydrateAfterLogin();
                nav('/app', { replace: true });
              }}
              onGoLogin={() => {
                setVerify({
                  open: false,
                  loginId: '',
                  email: '',
                  emailMasked: '',
                  resolving: false,
                });
                setErr(null);
              }}
              cooldownSeconds={60}
            />
          ) : (
            <MDBox
              component="form"
              role="form"
              onSubmit={onSubmit}
              sx={{ display: 'grid', gap: 2 }}
            >
              <MDInput
                type="text"
                label="E-posta veya kullanıcı adı"
                fullWidth
                autoFocus
                value={loginId}
                disabled={inputsDisabled}
                onChange={(e) => setLoginId(e.target.value)}
                autoComplete="username"
                inputProps={{ inputMode: 'text' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon fontSize="small">person</Icon>
                    </InputAdornment>
                  ),
                }}
              />

              <MDInput
                type="password"
                label="Şifre"
                fullWidth
                value={password}
                disabled={inputsDisabled}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon fontSize="small">lock</Icon>
                    </InputAdornment>
                  ),
                }}
              />

              {err && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {err}
                </Alert>
              )}

              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 0.25, gap: 1, flexWrap: 'wrap' }}
              >
                <MDBox display="flex" alignItems="center" gap={0.75} sx={{ ml: -0.75 }}>
                  <Switch
                    checked={rememberMe}
                    disabled={inputsDisabled}
                    onChange={() => setRememberMe((v) => !v)}
                  />
                  <MDTypography variant="button" color="text" sx={{ opacity: 0.85 }}>
                    Beni hatırla
                  </MDTypography>
                </MDBox>

                <MDTypography
                  component={Link}
                  to="/forgot-password"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  sx={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
                >
                  Şifremi unuttum
                </MDTypography>
              </MDBox>

              <MDButton
                type="submit"
                variant="gradient"
                color="info"
                fullWidth
                disabled={!canSubmit}
                sx={{
                  mt: 0.5,
                  py: 1.3,
                  borderRadius: '14px',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                }}
              >
                {loading ? (
                  <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <CircularProgress size={16} thickness={5} />
                    <span>Giriş yapılıyor…</span>
                  </MDBox>
                ) : (
                  'Giriş Yap'
                )}
              </MDButton>

              <Divider sx={{ my: 0.5 }} />

              <MDBox textAlign="center" sx={{ display: 'grid', gap: 0.75 }}>
                <MDTypography variant="button" color="text" sx={{ opacity: 0.8 }}>
                  Hesabın yok mu?
                </MDTypography>

                <MDTypography
                  component={Link}
                  to="/sign-up"
                  variant="button"
                  color="info"
                  fontWeight="bold"
                  sx={{ textDecoration: 'none' }}
                >
                  Hesap oluştur
                </MDTypography>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      </Card>

      <PlanBlockedModal
        open={planModal.open}
        onClose={() => setPlanModal({ open: false, code: '', message: '' })}
        code={planModal.code}
        message={planModal.message}
        phone={SUPPORT_PHONE}
        waE164={SUPPORT_WA_E164}
      />
    </AuthBasicLayout>
  );
}
